import { useUserLogin } from '@/features/user/user.mutations';
import { getUserOptions } from '@/features/user/user.queries';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect, useRouter, useSearch } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import FormTextInput from '@/components/form-inputs/form-text-input';

const LoginFormObject = z.object({
  username: z.string().min(1, 'Username is required').default(''),
  password: z.string().min(1, 'Password is required').default(''),
});

export type LoginForm = z.infer<typeof LoginFormObject>;

const loginRedirect = z.object({
  redirect: z.string().catch('/').optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: loginRedirect,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (user) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: LoginForm,
});

function LoginForm() {
  const { t } = useTranslation(['form_field']);
  const router = useRouter();

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

  const tenantName = window.location.host.split('.')[0];

  const { redirect } = useSearch({ strict: false });
  const { mutateAsync: loginMutateAsync, isPending } = useUserLogin();

  const form = useForm<LoginForm>({
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
    },
    resolver: zodResolver(LoginFormObject),
  });

  const handleSignIn = async (credentials: LoginForm) => {
    await loginMutateAsync(credentials);
    router.history.push(redirect ?? '/');
  };

  return (
    <div className='flex h-screen flex-1 flex-col items-center justify-center'>
      <Card className='p-4'>
        <CardHeader>
          <img
            src={`${API_BASE_URL}/public/tenants/logo/${tenantName}`}
            className='mx-auto block h-auto max-h-32 w-auto max-w-[460px] object-contain'
            alt='Logo'
          />
        </CardHeader>
        <CardContent>
          <div className='flex flex-1 items-center justify-center'>
            <Form {...form}>
              <form className='space-y-4 overflow-y-auto p-6'>
                <FormTextInput
                  name={'username'}
                  label={t('label.username')}
                  placeholder={t('label.username')}
                />
                <FormTextInput
                  isPassword
                  name={'password'}
                  label={t('label.password')}
                  placeholder={t('label.password')}
                />
                <div className='mt-2 flex items-center justify-center'>
                  <Button onClick={form.handleSubmit(handleSignIn)} className='w-full rounded-xl'>
                    <span>Σύνδεση</span>
                    {isPending && <Loader2 className='animate-spin' size={24} />}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
